import React, { ReactNode } from 'react';
import {
  Snackbar,
  SnackbarCloseButton,
  SnackbarIcon,
  SnackbarMessage,
  SnackbarProvider,
} from '@bice_vida/ds-components-snackbar';
import DevStageAlert from '@components/DevStageAlert';
import Header from '@components/Header';
import StepFooter, { StepFooterPropsI } from '@components/StepFooter';
import useStore from '@hooks/useStore';
import PortalImage from '@images/referrals/portalfunerarias.webp';
import { useAgentStore, useLeadStore, useSnackbarStore } from '@stores';
import BackIcon from '@svg/icon_back.svg';
import classNames from 'classnames';
import Image from 'next/image';

import styles from './FlowLayout.module.scss';

const STAGE = process.env.STAGE || '';

export interface FlowLayoutPropsI {
  id: string;
  dataTestid: string;
  headerComponent?: JSX.Element;
  headerClassName?: string;
  contentClassName?: string;
  stepFooterProps: StepFooterPropsI;
  children: ReactNode;
  priceButton?: boolean;
  marginContent?: boolean;
}

const secondLogoData = {
  portalfunerarias: {
    image: PortalImage,
    width: 200,
    height: 100,
    assistWidth: 150,
  },
};

const FlowLayout = ({
  id,
  dataTestid,
  stepFooterProps,
  children,
  contentClassName,
  marginContent = true,
}: FlowLayoutPropsI): JSX.Element => {
  const agent = useStore(useAgentStore, (state) => state.agent);
  const { referral } = useLeadStore((state) => state.lead);
  const { onClose, isOpen, variant, message } = useSnackbarStore((state) => state);

  const secondLogo = secondLogoData[referral];

  const assistedBy = secondLogo?.image ? (
    <Image src={secondLogo.image} alt="assist logo" width={secondLogo.assistWidth} />
  ) : (
    agent?.name
  );

  const contentClassNames = classNames(styles.ttt_flow_layout_content, contentClassName, {
    [styles.ttt_flow_layout_content__margin]: marginContent,
    [styles.ttt_flow_layout_content__with_alert]: STAGE === 'dev',
  });

  return (
    <div data-testid={dataTestid} id={id} className={styles.ttt_flow_layout}>
      <div className={styles.ttt_flow_layout_layout_container}>
        <div className={styles.ttt_flow_layout_header_container}>
          <Header {...{ secondLogo }} />
        </div>
        <div className={styles.ttt_flow_layout_back_btn_container}>
          <a
            id="back-button"
            className={styles.ttt_flow_layout_back_btn}
            onClick={stepFooterProps?.backBtn?.onClick}
            role="button"
            tabIndex={0}
          >
            <Image src={BackIcon} height={60} width={60} alt="Volver" />
          </a>
        </div>
        <DevStageAlert />
        <div className={contentClassNames}>{children}</div>
        <SnackbarProvider duration={5000} swipeDirection={'up'}>
          <Snackbar onClose={onClose} isOpen={isOpen}>
            {<SnackbarIcon variant={variant} />}
            <SnackbarMessage>{message}</SnackbarMessage>

            {<SnackbarCloseButton />}
          </Snackbar>
        </SnackbarProvider>
      </div>

      <div className={styles.ttt_flow_layout_step_footer}>
        <StepFooter {...stepFooterProps} assistedBy={assistedBy} />
      </div>
    </div>
  );
};

export default FlowLayout;
